import { useMemo } from "react";
import { usePermissions } from "../../../../session/permissions";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { activeModuleIds, type ServiceModuleId } from "../../../service/service-modules";
import { serviceKeys } from "../../../service/service-keys";
import { useServiceQuery } from "../../../service/use-service-query";
import { useServiceDesk } from "./use-service-desk";

/**
 * The Service modules a workflow can depend on (backend `WORKFLOW_TRIGGER_MODULES` and `WORKFLOW_STEP_MODULES`; scope TKT-203).
 * The ticketing triggers and steps are not listed: tickets are always on. A default of a module that is switched off is
 * paused by the module switch, never deleted, and starts again by itself when the module is switched back on.
 */
const TRIGGER_MODULES: Readonly<Record<string, ServiceModuleId>> = {
  order_placed: "orders",
  order_status_changed: "orders",
  order_approval_requested: "orders",
  order_approval_decided: "orders",
  order_sync_failed: "orders",
  quote_status_changed: "orders",
  catalogue_sync_finished: "orders",
  visit_scheduled: "visits",
  visit_status_changed: "visits",
  visit_reminder_due: "visits",
  asset_maintenance_due: "assets",
  asset_warranty_expiring: "assets",
  contract_expiring: "assets",
  rma_status_changed: "assets",
};

const STEP_MODULES: Readonly<Record<string, ServiceModuleId>> = {
  lookup_asset: "assets",
  schedule_visit: "visits",
  lookup_catalogue: "orders",
  check_order_status: "orders",
  show_catalogue: "orders",
  create_order: "orders",
  show_order_status: "orders",
};

/** The module a trigger needs, or `undefined` for a trigger that needs none. */
export const moduleOfTrigger = (trigger: string): ServiceModuleId | undefined => TRIGGER_MODULES[trigger];

/** The first module a trigger or one of the step types needs, or `undefined`. */
export function moduleOfWorkflow(trigger: string, stepTypes: readonly string[] = []): ServiceModuleId | undefined {
  return TRIGGER_MODULES[trigger] ?? stepTypes.map((type) => STEP_MODULES[type]).find((module) => module !== undefined);
}

export interface WorkflowModules {
  /** The switches have been read: until then nothing is called "off". */
  known: boolean;
  /** The module is switched off (or not active) for the workspace. */
  isOff: (module: ServiceModuleId) => boolean;
}

/**
 * The workspace's module switches, read from `GET /v1/service-settings` once the Service Desk is known to be available
 * (the same call and cache as the Service pages). Unknown until read, and for a member who may not view tickets.
 */
export function useWorkflowModules(): WorkflowModules {
  const ws = useWorkspaceId();
  const permitted = usePermissions().can("tickets.view");
  const available = useServiceDesk().state === "available";
  const settings = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "general"),
    enabled: permitted && available,
    staleTime: 30_000,
    request: (api, signal) => api.GET("/v1/service-settings", { signal }),
  });
  return useMemo<WorkflowModules>(() => {
    if (!settings.data) return { known: false, isOff: () => false };
    const on = activeModuleIds(settings.data.modules);
    return { known: true, isOff: (module) => module !== "tickets" && !on.includes(module) };
  }, [settings.data]);
}
